// messages — what goes where (pen "Messages" §1, "What goes where"), pure.
//
// Every notice is classified into one situation; the situation picks where
// it shows. Toasts are for what you can't see from where you are: a step that
// worked where you can see it gets the quiet last-step line, a problem with
// one object is shown at the object.
//
//   situation                 where (design)            where today
//   worked, visible           last-step line            last-step line
//   worked, out of view       toast with "Show"         toast
//   object problem            at the object             at the object
//   field problem             at the field              toast (*)
//   waited-for finished       toast with its action     toast
//   background finished       Events                    toast (*)
//   background problem        Events + the bell dot     toast (*)
//   assistant work            at the object + the chat  toast (*)
//   connection                the menu row              toast (*)
//   unknown (default)         Events                    toast (*)
//
// (*) There is no Events list, bell or field anchor for these notices yet,
// so they stay a normal toast; `surfaceOf` is the one place to change when
// those land.

export { messageInput, situationOf };

import { exhaustSwitchCase } from "@bilda/utils";
import type { HostNotice } from "./frame.js";
import type { MessageInput } from "./messageQueue.js";

type MessageSituation =
    | "worked-visible"
    | "worked-out-of-view"
    | "object-problem"
    | "field-problem"
    | "waited-for-finished"
    | "background-finished"
    | "background-problem"
    | "assistant-work"
    | "connection"
    | "unknown";

// Toasts stay 6 s; a step that worked shows 4 s on the last-step line. One
// that carries an Undo stays longer, so the Undo can be reached (there is no
// Undo pill yet; until there is, the Undo rides on the message).
const TOAST_MS = 6000;
const LAST_STEP_MS = 4000;
const WITH_UNDO_MS = 8000;
const AT_OBJECT_MS = 2400;

const situationOf = (n: HostNotice): MessageSituation => {
    switch (n.type) {
        case "module-made":
        case "course-made":
        case "parts-added":
        case "part-moved":
        case "part-removed":
        case "removed-from-canvas":
            return "worked-visible";
        // The same file again: on the canvas its card is right there.
        case "source-deduped":
            return n.onCanvas ? "object-problem" : "worked-out-of-view";
        case "resource-deleted":
        case "conversation-deleted":
        case "conversation-archived":
        case "project-archived":
        case "sources-kept":
        case "copied":
        case "feedback-sent":
            return "worked-out-of-view";
        case "edit-conflict":
        case "suggestion-outdated":
        case "undo-refused":
        case "redo-refused":
        case "already-part":
        case "edit-failed":
        case "lifecycle-failed":
        case "save-failed":
            return "object-problem";
        case "copy-failed":
            return "field-problem";
        case "source-restored":
        case "exported":
            return "waited-for-finished";
        case "ingest-failed":
        case "replay-degraded":
            return "background-problem";
        case "turn-failed":
            return "assistant-work";
        // An offer, not a result: the design's default (Events), a toast
        // today.
        case "tour-offered":
        case "attach":
        case "reference":
        case "media":
        case "dev-started":
        case "dev-succeeded":
        case "dev-failed":
            return "unknown";
        default:
            return exhaustSwitchCase(n);
    }
};

// Where a situation shows today. An object problem shows at the object only
// when the notice names an object on the canvas; otherwise it is a toast.
const surfaceOf = (n: HostNotice): MessageInput<HostNotice>["surface"] => {
    const situation = situationOf(n);
    switch (situation) {
        case "worked-visible":
            return "last-step";
        case "object-problem":
            return n.type === "already-part" || n.type === "source-deduped"
                ? "at-object"
                : "toast";
        case "worked-out-of-view":
        case "field-problem":
        case "waited-for-finished":
        case "background-finished":
        case "background-problem":
        case "assistant-work":
        case "connection":
        case "unknown":
            return "toast";
        default:
            return exhaustSwitchCase(situation);
    }
};

// What it is about, for merging repeats (dedupe key = kind + target).
const targetOf = (n: HostNotice): string => {
    switch (n.type) {
        case "turn-failed":
            return n.failure.type;
        case "ingest-failed":
            return n.filename;
        case "source-deduped":
            return n.sourceId;
        case "removed-from-canvas":
            return n.ids.join(" ");
        case "resource-deleted":
            return n.ref.id;
        case "conversation-deleted":
        case "conversation-archived":
            return n.conversationId;
        case "project-archived":
            return n.projectId;
        case "sources-kept":
            return n.titles.join(" ");
        case "tour-offered":
            return n.view;
        case "save-failed":
            return `${n.retry.type} ${n.retry.id}`;
        case "module-made":
        case "course-made":
            return n.ref.id;
        case "parts-added":
        case "part-moved":
        case "part-removed":
        case "already-part":
            return n.moduleId;
        case "edit-conflict":
            return `${n.materialId} ${n.elementId}`;
        case "lifecycle-failed":
            return n.message;
        case "copied":
            return n.label;
        case "feedback-sent":
            return "";
        case "source-restored":
            return n.sourceTitle ?? "";
        case "exported":
            return n.filename;
        case "dev-started":
        case "dev-succeeded":
        case "dev-failed":
            return n.label;
        case "attach":
        case "reference":
        case "media":
        case "suggestion-outdated":
        case "undo-refused":
        case "redo-refused":
        case "edit-failed":
        case "copy-failed":
        case "replay-degraded":
            return "";
        default:
            return exhaustSwitchCase(n);
    }
};

// Errors that need you stay until closed.
const needsYou = (n: HostNotice): boolean => {
    switch (n.type) {
        case "edit-conflict":
        case "suggestion-outdated":
        case "undo-refused":
        case "redo-refused":
        case "edit-failed":
        case "turn-failed":
        case "ingest-failed":
        case "lifecycle-failed":
        case "save-failed":
            return true;
        default:
            return false;
    }
};

const hasUndo = (n: HostNotice): boolean => {
    switch (n.type) {
        case "resource-deleted":
        case "conversation-deleted":
        case "conversation-archived":
        case "project-archived":
        case "module-made":
        case "course-made":
        case "parts-added":
        case "part-moved":
        case "part-removed":
        case "removed-from-canvas":
            return true;
        default:
            return false;
    }
};

const messageInput = (n: HostNotice): MessageInput<HostNotice> => {
    const surface = surfaceOf(n);
    const dwell = hasUndo(n)
        ? WITH_UNDO_MS
        : surface === "last-step"
          ? LAST_STEP_MS
          : surface === "at-object"
            ? AT_OBJECT_MS
            : TOAST_MS;
    return {
        surface,
        key: `${n.type}\u0000${targetOf(n)}`,
        payload: n,
        sticky: needsYou(n),
        dwell,
    };
};
